import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Container, Button, Paper, CircularProgress, Grid, Card, CardContent, Divider, Chip, List, ListItem, ListItemIcon, ListItemText, useTheme } from '@mui/material';
import { 
  UploadCloud, 
  CheckCircle, 
  FileText, 
  CheckSquare, 
  AlertCircle, 
  File, 
  RefreshCw,
  ShieldAlert
} from 'lucide-react';
import { API_BASE_URL } from '../config';

const loadingSteps = [
  "Uploading agreement PDF to server...",
  "Extracting PDF text content...",
  "Initializing legal AI analyzer model...",
  "Scanning document for lock-in period details...",
  "Auditing liability and security deposit clauses...",
  "Synthesizing tenant rights checklist...",
  "Formatting red flag report..."
];

const RentAgreementDetector = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const mode = theme.palette.mode;

  useEffect(() => {
    document.title = "Rent Agreement Red Flag Detector | CoreTechTalks";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Upload your rent agreement PDF to instantly get a plain English summary, detect hidden risky clauses, and review a comprehensive tenant rights checklist.");
    }
  }, []);

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setLoadingStep((prev) => (prev + 1) % loadingSteps.length);
      }, 3000);
    } else {
      setLoadingStep(0);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type !== "application/pdf") {
        setError("Please upload a valid PDF file.");
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type !== "application/pdf") {
        setError("Please upload a valid PDF file.");
        return;
      }
      setFile(droppedFile);
      setError(null);
    }
  };

  const analyzeAgreement = async () => {
    if (!file) {
      setError("Please select a file first.");
      return;
    }
    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE_URL}/api/rent-agreement/analyze`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to analyze agreement.");
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (score) => {
    if (score >= 8) return theme.palette.error.main;   // Red
    if (score >= 4) return theme.palette.warning.main; // Orange
    return theme.palette.success.main;                 // Green
  };

  const getRiskLabel = (score) => {
    if (score >= 8) return 'High Risk';
    if (score >= 4) return 'Medium Risk';
    return 'Low Risk';
  };

  // Count risks
  const getRiskCounts = () => {
    if (!result || !result.risky_clauses) return { high: 0, med: 0, low: 0 };
    return result.risky_clauses.reduce((acc, clause) => {
      if (clause.risk_score >= 8) acc.high++;
      else if (clause.risk_score >= 4) acc.med++;
      else acc.low++;
      return acc;
    }, { high: 0, med: 0, low: 0 });
  };

  const riskCounts = getRiskCounts();

  return (
    <Box sx={{ flexGrow: 1, py: { xs: 8, md: 10 }, minHeight: 'calc(100vh - 200px)', position: 'relative' }}>
      {/* Decorative Orbs */}
      <Box className="glow-orb" sx={{ width: '400px', height: '400px', top: '-100px', right: '-100px', background: 'rgba(99, 102, 241, 0.08)' }} />
      <Box className="glow-orb" sx={{ width: '300px', height: '300px', bottom: '10%', left: '-150px', background: 'rgba(6, 182, 212, 0.08)' }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box textAlign="center" mb={8}>
          <Typography 
            variant="h1" 
            component="h1" 
            sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '32px', sm: '42px', md: '48px' } }}
          >
            Rent Agreement <span className="gradient-text">Red Flag Detector</span>
          </Typography>
          <Typography variant="h6" color="text.secondary" maxWidth="760px" mx="auto" sx={{ lineHeight: 1.6, fontWeight: 500 }}>
            Upload your draft rent agreement PDF. Our Legal AI will instantly summarize the document, highlight hidden liabilities or unfair lock-in clauses, and output a checklist of standard tenant rights.
          </Typography>
        </Box>

        {!result && (
          <Box sx={{ maxWidth: '650px', mx: 'auto' }}>
            <Paper 
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{ 
                p: { xs: 4, md: 6 }, 
                textAlign: 'center', 
                borderRadius: '24px', 
                border: `2px dashed ${dragActive ? theme.palette.primary.main : (mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)')}`,
                backgroundColor: dragActive 
                  ? (mode === 'light' ? 'rgba(99, 102, 241, 0.04)' : 'rgba(99, 102, 241, 0.08)')
                  : (mode === 'light' ? '#ffffff' : 'rgba(17, 22, 37, 0.4)'),
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                boxShadow: mode === 'light' ? '0 10px 30px rgba(0,0,0,0.02)' : '0 10px 40px rgba(0,0,0,0.2)',
                position: 'relative',
                '&:hover': { 
                  borderColor: 'primary.main', 
                  bgcolor: mode === 'light' ? 'rgba(99, 102, 241, 0.02)' : 'rgba(99, 102, 241, 0.04)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <input 
                type="file" 
                accept=".pdf" 
                ref={fileInputRef} 
                style={{ display: 'none' }} 
                onChange={handleFileChange}
              />
              
              {file ? (
                <Box sx={{ py: 2 }}>
                  <Box 
                    sx={{ 
                      display: 'inline-flex', 
                      p: 2.5, 
                      borderRadius: '20px', 
                      bgcolor: 'rgba(99, 102, 241, 0.1)', 
                      color: 'primary.main',
                      mb: 2.5
                    }}
                  >
                    <File size={48} />
                  </Box>
                  <Typography variant="h5" fontWeight="800" sx={{ mb: 1, px: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </Typography>
                  <Typography color="text.secondary" variant="body2" sx={{ mb: 4 }}>
                    {(file.size / (1024 * 1024)).toFixed(2)} MB • PDF Document
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Box 
                    sx={{ 
                      display: 'inline-flex', 
                      p: 2.5, 
                      borderRadius: '20px', 
                      bgcolor: mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)', 
                      color: 'text.secondary',
                      mb: 2.5
                    }}
                  >
                    <UploadCloud size={48} />
                  </Box>
                  <Typography variant="h5" fontWeight="800" sx={{ mb: 1 }}>
                    Drag and drop your PDF here
                  </Typography>
                  <Typography color="text.secondary" variant="body2" sx={{ mb: 4 }}>
                    or click to browse local files (Supports PDF formats up to 10MB)
                  </Typography>
                </Box>
              )}
              
              {file && !loading && (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={(e) => {
                      e.stopPropagation();
                      analyzeAgreement();
                    }}
                    sx={{ px: 5, py: 1.5 }}
                  >
                    Analyze Agreement
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    sx={{ px: 4 }}
                  >
                    Clear File
                  </Button>
                </Box>
              )}

              {loading && (
                <Box sx={{ py: 2 }}>
                  <CircularProgress size={36} color="primary" sx={{ mb: 2 }} />
                  <Typography 
                    variant="body1" 
                    fontWeight="600" 
                    color="text.primary" 
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                  >
                    <RefreshCw size={16} className="animated-float" />
                    {loadingSteps[loadingStep]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    This typically takes 10-15 seconds. Please do not close this window.
                  </Typography>
                </Box>
              )}
              
              {error && (
                <Typography color="error" sx={{ mt: 3, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <AlertCircle size={16} />
                  {error}
                </Typography>
              )}
            </Paper>
          </Box>
        )}

        {result && (
          <Box>
            {/* Results Header */}
            <Box mb={5} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box>
                <Typography variant="h3" component="h2" sx={{ fontWeight: 800 }}>Audit Report Summary</Typography>
                <Typography variant="body2" color="text.secondary">Document verified successfully using Legal AI model</Typography>
              </Box>
              <Button 
                variant="outlined" 
                startIcon={<RefreshCw size={16} />}
                onClick={() => { setResult(null); setFile(null); }}
                sx={{ borderRadius: '12px' }}
              >
                Audit Another PDF
              </Button>
            </Box>

            <Grid container spacing={4}>
              {/* Summary Dashboard Panel */}
              <Grid item xs={12}>
                <Card sx={{ p: 1 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <Typography variant="h5" fontWeight="800" sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'primary.main', gap: 1 }}>
                          <FileText size={22} /> Executive Summary
                        </Typography>
                        <Typography variant="body1" color="text.primary" lineHeight={1.7}>
                          {result.summary}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4} sx={{ borderLeft: { md: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}` }, pl: { md: 4 } }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Risk Distribution
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1.5}>
                          <Box display="flex" justifyItems="center" justifyContent="space-between">
                            <Chip size="small" label="High Risk Clauses" color="error" sx={{ fontWeight: 700, fontSize: '11px' }} />
                            <Typography fontWeight="800" color="error.main">{riskCounts.high}</Typography>
                          </Box>
                          <Box display="flex" justifyItems="center" justifyContent="space-between">
                            <Chip size="small" label="Medium Risk Clauses" color="warning" sx={{ fontWeight: 700, fontSize: '11px' }} />
                            <Typography fontWeight="800" color="warning.main">{riskCounts.med}</Typography>
                          </Box>
                          <Box display="flex" justifyItems="center" justifyContent="space-between">
                            <Chip size="small" label="Standard Clauses" color="success" sx={{ fontWeight: 700, fontSize: '11px' }} />
                            <Typography fontWeight="800" color="success.main">{riskCounts.low}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Red Flags Clauses Column */}
              <Grid item xs={12} md={7}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="800" sx={{ display: 'flex', alignItems: 'center', mb: 3.5, color: 'error.main', gap: 1 }}>
                      <ShieldAlert size={22} /> Audited Red Flags
                    </Typography>
                    
                    {result.risky_clauses && result.risky_clauses.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                        {result.risky_clauses.map((clause, idx) => (
                          <Paper 
                            key={idx} 
                            elevation={0}
                            sx={{ 
                              p: 3, 
                              borderRadius: '16px', 
                              borderLeft: `5px solid ${getRiskColor(clause.risk_score)}`, 
                              bgcolor: mode === 'light' ? 'rgba(0,0,0,0.015)' : 'rgba(255,255,255,0.015)',
                              border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'}`,
                              borderLeftWidth: '5px'
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                              <Chip 
                                label={`${getRiskLabel(clause.risk_score)} (Severity ${clause.risk_score}/10)`} 
                                size="small"
                                sx={{ 
                                  backgroundColor: getRiskColor(clause.risk_score), 
                                  color: '#ffffff', 
                                  fontWeight: 700,
                                  fontSize: '11px'
                                }} 
                              />
                            </Box>
                            <Typography variant="body1" fontWeight="700" color="text.primary" sx={{ mb: 2, fontStyle: 'italic', lineHeight: 1.5 }}>
                              "{clause.clause_text}"
                            </Typography>
                            <Divider sx={{ my: 2, borderColor: mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, lineHeight: 1.6 }}>
                              <AlertCircle size={16} color={getRiskColor(clause.risk_score)} style={{ marginTop: 2, minWidth: 16 }} />
                              <span>{clause.reason}</span>
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Typography color="text.secondary" fontStyle="italic">No major red flags detected in this agreement.</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Tenant Rights Checklist Column */}
              <Grid item xs={12} md={5}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    background: mode === 'light' 
                      ? 'linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 100%)' 
                      : 'linear-gradient(180deg, rgba(22, 163, 74, 0.05) 0%, rgba(17, 22, 37, 0.5) 100%)',
                    border: `1px solid ${mode === 'light' ? 'rgba(22, 163, 74, 0.15)' : 'rgba(22, 163, 74, 0.1)'}`
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="800" sx={{ display: 'flex', alignItems: 'center', mb: 3.5, color: 'success.main', gap: 1 }}>
                      <CheckSquare size={22} /> Tenant Guard Checklist
                    </Typography>
                    
                    {result.tenant_checklist && result.tenant_checklist.length > 0 ? (
                      <List disablePadding>
                        {result.tenant_checklist.map((item, idx) => (
                          <ListItem key={idx} sx={{ px: 0, py: 1.5, alignItems: 'flex-start', gap: 1.5 }}>
                            <ListItemIcon sx={{ minWidth: 'auto', mt: 0.5, color: 'success.main' }}>
                              <CheckCircle size={18} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={item} 
                              primaryTypographyProps={{ 
                                variant: 'body2', 
                                color: 'text.primary', 
                                fontWeight: 600,
                                lineHeight: 1.6
                              }} 
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography color="text.secondary" fontStyle="italic">Checklist not available.</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default RentAgreementDetector;
